import {
  TableContainer,
  Table,
  Tbody,
  Thead,
  Tr,
  Td,
  Th,
  Tooltip,
  TypographyProps,
} from '@chakra-ui/react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import * as _ from 'lodash';
import React, { MutableRefObject, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  CreditFacilityGQLType,
  EventWithActorGQLType,
  FundingRequestWithFullDetailsGQLType,
} from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { FundingRequestStatusBadge } from '../shared/StatusBadge';
import { colors } from 'packages/common-frontend/colors';
import {
  CsvXlsxDownloadSelect,
  getTanstackVisibleDataForDownload,
} from 'packages/common-frontend/components/CsvXlsxDownloadSelect';
import { ColumnDisplaySettingsButton } from 'packages/common-frontend/components/TanStack/ColumnReordering';
import {
  createDefaultTableColumnConfiguration,
  useTableColumnPreferences,
} from 'packages/common-frontend/hooks/useTableColumnPreferences';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

type FundingRequest = Pick<
  FundingRequestWithFullDetailsGQLType,
  | 'id'
  | 'created_at'
  | 'drawRequestTotalAmount'
  | 'collectionsPaydownTotalAmount'
  | 'collectionsRecycleTotalAmount'
  | 'curePaydownTotalAmount'
  | 'status'
  | 'borrowingBaseReport'
  | 'type'
> & {
  creditFacility: Pick<CreditFacilityGQLType, 'id' | 'name'>;
  fundingRequestEvents: Pick<
    EventWithActorGQLType,
    'created_at' | 'actionKey'
  >[];
  reportingDate: Util.FullDateStr;
};

// Exports
type Props = {
  fundingRequests: FundingRequest[];
  columnDisplaySettingsButtonRef: MutableRefObject<HTMLDivElement>;
};

const columnDefs: Array<
  ColumnDef<FundingRequest, any> & {
    id: string;
    hideFromValley?: boolean;
    onlyForValley?: boolean;
    headerTextAlign?: TypographyProps['textAlign'];
  }
> = [
  {
    id: 'fundingRequestId',
    accessorKey: 'id',
    header: 'Funding Request Id',
    enableSorting: false,
    cell: function FundingRequestID(cellContext) {
      const id = cellContext.getValue();
      return (
        <Td color={colors.info}>
          <RouterLink to={`/funding-requests/${id}`}>{id}</RouterLink>
        </Td>
      );
    },
  },
  {
    id: 'borrowingBaseReportingDate',
    accessorFn: (row) =>
      commonDate.fullDateStrToHumanDateString(row.reportingDate),
    header: 'Borrowing Base Reporting Date',
    meta: { headerTextAlign: 'left' },
    enableSorting: false,
    cell: function BorrowingBaseReportingDate({ getValue }) {
      return <Td textAlign="left">{getValue()}</Td>;
    },
    hideFromValley: true /* @VLYSLS @jendevelops - temp flag */,
  },
  {
    id: 'fundingRequestDate',
    accessorKey: 'fundingRequestEvents',
    accessorFn: (row) =>
      commonDate.dateToHumanDateString(
        new Date(
          /* the submission date will use event data or default to created_at date when unavailable */
          _.find(
            row.fundingRequestEvents,
            (event) => event.actionKey === 'requestSubmitted',
          )?.created_at || row.created_at,
        ),
      ),
    header: 'Funding Request Date',
    meta: { headerTextAlign: 'left' },
    enableSorting: false,
    cell: function FundingRequestsDateCell({ getValue }) {
      return <Td textAlign="left">{getValue()}</Td>;
    },
  },
  {
    id: 'creditFacility',
    accessorFn: (row) => row.creditFacility.name,
    header: 'Credit Facility',
    meta: { headerTextAlign: 'left' },
    enableSorting: false,
    cell: function CreditFacilityCell({ getValue }) {
      return <Td textAlign="left">{getValue()}</Td>;
    },
  },
  {
    id: 'fundingRequestType',
    accessorKey: 'type',
    header: 'Request Type',
    meta: { headerTextAlign: 'left' },
    enableSorting: false,
    cell: function RequestTypeCell({ getValue }) {
      const type = getValue();
      const label = type === 'origination' ? 'Origination' : 'Upsize';
      return <Td textAlign="left">{commonUtils.toTitleCase(label)}</Td>;
    },
    onlyForValley: true /* @VLYSLS @jendevelops - temp flag */,
  },
  {
    id: 'draw',
    accessorKey: 'drawRequestTotalAmount',
    header: 'Draw',
    meta: { headerTextAlign: 'center' },
    enableSorting: false,
    cell: function DrawCell({ getValue }) {
      return <Td textAlign="center">{dollarNumberWithCommas(getValue())}</Td>;
    },
  },
  {
    id: 'recycle',
    accessorKey: 'collectionsRecycleTotalAmount',
    header: 'Recycle',
    meta: { headerTextAlign: 'center' },
    enableSorting: false,
    cell: function RecycleCell({ getValue }) {
      return <Td textAlign="center">{dollarNumberWithCommas(getValue())}</Td>;
    },
    hideFromValley: true /* @VLYSLS @jendevelops - temp flag */,
  },
  {
    id: 'collectionsPaydown',
    accessorKey: 'collectionsPaydownTotalAmount',
    header: 'Collections Paydown',
    meta: { headerTextAlign: 'center' },
    enableSorting: false,
    cell: function CollectionsPaydownCell({ getValue }) {
      return <Td textAlign="center">{dollarNumberWithCommas(getValue())}</Td>;
    },
    hideFromValley: true /* @VLYSLS @jendevelops - temp flag */,
  },
  {
    id: 'curePaydown',
    accessorKey: 'curePaydownTotalAmount',
    header: 'Cure Paydown',
    meta: { headerTextAlign: 'center' },
    enableSorting: false,
    cell: function CollectionsPaydownCell({ getValue }) {
      return <Td textAlign="center">{dollarNumberWithCommas(getValue())}</Td>;
    },
    hideFromValley: true /* @VLYSLS @jendevelops - temp flag */,
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    meta: { headerTextAlign: 'center' },
    enableSorting: false,
    cell: function StatusCell({ getValue }) {
      return (
        <Td>
          <FundingRequestStatusBadge status={getValue()} size="sm" />
        </Td>
      );
    },
  },
];

const TABLE_KEY = 'fundingRequests.historyTable';

export const HistoryTable = ({
  fundingRequests,
  columnDisplaySettingsButtonRef,
}: Props) => {
  // @VLYSLS @jendevelops conditionally hide borrowing base dependent values if no cfs w/ borrowing base
  const { isSlsAgentLender } = useFeatureFlags();

  const filteredColumnDefs = useMemo(
    () =>
      isSlsAgentLender
        ? columnDefs.filter((columnDef) => !columnDef.hideFromValley)
        : columnDefs.filter((columnDef) => !columnDef.onlyForValley),
    [isSlsAgentLender],
  );

  const defaultTableConfig = createDefaultTableColumnConfiguration({
    tableKey: TABLE_KEY,
    columns: filteredColumnDefs.map((column) => ({
      columnKey: column.id,
      isVisible: true,
      isFixed: false,
    })),
  });

  const columnNames = useMemo(
    () =>
      filteredColumnDefs.reduce(
        (acc, columnDef) => ({ ...acc, [columnDef.id]: columnDef.header }),
        {},
      ),
    [filteredColumnDefs],
  );

  const {
    columnConfiguration,
    columnOrder,
    columnVisibility,
    updateUserColumnConfiguration,
  } = useTableColumnPreferences({
    tableKey: TABLE_KEY,
    defaultTableColumnConfiguration: defaultTableConfig,
  });

  const table = useReactTable({
    data: fundingRequests,
    columns: filteredColumnDefs,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility,
      columnOrder,
    },
  });

  const csvDownload = getTanstackVisibleDataForDownload({
    tableInstance: table,
  });

  const [hasPortalRef, setHasPortalRef] = useState(false);

  useEffect(() => {
    // When this component is rendered, it's possible that this ref is still null.
    // After rendering (when this useEffect is run), we set hasPortalRef to rerender
    // this component when this ref is present.
    if (columnDisplaySettingsButtonRef.current) setHasPortalRef(true);
  }, [columnDisplaySettingsButtonRef]);

  return (
    <>
      {hasPortalRef &&
        //  This portal is rendered in the parent component, ViewContainer
        createPortal(
          <>
            <ColumnDisplaySettingsButton
              initialColumns={columnConfiguration}
              updateColumnConfigs={updateUserColumnConfiguration}
              columnNames={columnNames}
            />
            <Tooltip hasArrow label="Download">
              <span>
                <CsvXlsxDownloadSelect
                  dataRows={csvDownload.dataRows}
                  headers={csvDownload.headers}
                  filenamePrefix="funding-requests"
                  linkText=""
                  MenuButtonProps={{ sx: { span: { marginInlineEnd: 0 } } }}
                />
              </span>
            </Tooltip>
          </>,
          columnDisplaySettingsButtonRef.current,
        )}
      <TableContainer whiteSpace="normal">
        <Table cursor="default">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th
                    key={header.id}
                    width="10%"
                    textAlign={header.column.columnDef.meta?.headerTextAlign}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.original.id}>
                {row.getVisibleCells().map((cell) => (
                  <React.Fragment key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </React.Fragment>
                ))}
              </Tr>
            ))}
            {fundingRequests.length === 0 && (
              <Tr>
                <Td colSpan={columnDefs.length} textAlign="center">
                  No results found. Please adjust your filters and try again.
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};
